import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, LogOut, Plus, Download, Pencil, Trash2, Package, Users, Boxes, Search, Smile, Frown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DIRECTION_LABEL, TRIP_STATUS, TRACK_STATUS, statusLabel, useLang, CONTACT } from "@/i18n";
import { api, apiError, API } from "@/lib/api";
import { fmtGBP, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CapacityMeter } from "@/components/CapacityMeter";
import { TripDialog } from "@/components/admin/TripDialog";
import { BookingDialog } from "@/components/admin/BookingDialog";
import { SettingsTab } from "@/components/admin/SettingsTab";

const STATUS_STYLE = {
  completed: "bg-emerald-500/15 text-emerald-600",
  reweighed: "bg-blue-500/15 text-blue-600",
  booking_received: "bg-amber-500/15 text-amber-600",
  waiting_list: "bg-amber-500/15 text-amber-600",
  confirmed: "bg-blue-500/15 text-blue-600",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [trips, setTrips] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [filters, setFilters] = useState({ trip_id: "all", status: "all", direction: "all", search: "" });
  const [tripDialog, setTripDialog] = useState({ open: false, trip: null });
  const [bookingDialog, setBookingDialog] = useState({ open: false, booking: null });

  useEffect(() => { if (user === false) navigate("/admin"); }, [user, navigate]);

  const loadTrips = useCallback(async () => {
    const { data } = await api.get("/trips?all=true");
    setTrips(data);
  }, []);
  const loadStats = useCallback(async () => {
    const { data } = await api.get("/admin/stats"); setStats(data);
  }, []);
  const loadNotifs = useCallback(async () => {
    const { data } = await api.get("/admin/notifications"); setNotifs(data);
  }, []);
  const loadBookings = useCallback(async () => {
    const q = new URLSearchParams();
    if (filters.trip_id !== "all") q.set("trip_id", filters.trip_id);
    if (filters.status !== "all") q.set("status", filters.status);
    if (filters.direction !== "all") q.set("direction", filters.direction);
    if (filters.search) q.set("search", filters.search);
    const { data } = await api.get(`/admin/bookings?${q.toString()}`);
    setBookings(data);
  }, [filters]);

  useEffect(() => { if (user) { loadTrips(); loadStats(); loadNotifs(); } }, [user, loadTrips, loadStats, loadNotifs]);
  useEffect(() => { if (user) loadBookings(); }, [user, loadBookings]);

  const reloadAll = () => { loadTrips(); loadStats(); loadNotifs(); loadBookings(); };

  const deleteTrip = async (id) => {
    if (!window.confirm("Delete this trip?")) return;
    try { await api.delete(`/admin/trips/${id}`); toast.success("Trip deleted"); reloadAll(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const tripPayload = (tr, over) => ({
    direction: tr.direction, title: tr.title, status: tr.status,
    dropoff_address: tr.dropoff_address, pickup_address: tr.pickup_address,
    receive_deadline: tr.receive_deadline, deadline_timezone: tr.deadline_timezone,
    capacity_kg: tr.capacity_kg, standard_fee_gbp_per_kg: tr.standard_fee_gbp_per_kg,
    bulky_pricing_type: tr.bulky_pricing_type, bulky_fee_gbp: tr.bulky_fee_gbp,
    exchange_rate_gbp_to_idr: tr.exchange_rate_gbp_to_idr,
    active: tr.active !== false, order_open: tr.order_open !== false,
    ...over,
  });
  const setTripActive = async (tr, active) => {
    try { await api.put(`/admin/trips/${tr.id}`, tripPayload(tr, { active })); toast.success(active ? "Trip activated (visible on site)" : "Trip set inactive (hidden from site)"); reloadAll(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setTripOrders = async (tr, order_open) => {
    try { await api.put(`/admin/trips/${tr.id}`, tripPayload(tr, { order_open })); toast.success(order_open ? "Orders opened" : "Orders closed (trip stays visible)"); reloadAll(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const exportXlsx = async () => {
    try {
      const res = await api.get("/admin/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `jastip_report.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const markAllRead = async () => { await api.put("/admin/notifications/read-all"); loadNotifs(); };
  const unread = notifs.filter((n) => !n.read).length;
  const feedbackBookings = bookings.filter((b) => b.feedback);

  if (user == null) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading...</div>;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-primary text-primary-foreground grid place-items-center"><Package className="h-5 w-5" /></div>
            <span className="font-display font-bold hidden sm:block">Jastip Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" data-testid="export-btn" onClick={exportXlsx}><Download className="h-4 w-4 mr-1" /> Export</Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="notif-btn" className="relative">
                  <Bell className="h-5 w-5" />
                  {unread > 0 && <span data-testid="notif-count" className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-destructive text-white text-xs grid place-items-center">{unread}</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <div className="flex items-center justify-between p-3 border-b border-border">
                  <span className="font-semibold text-sm">Notifications</span>
                  <Button variant="ghost" size="sm" onClick={markAllRead}>Mark all read</Button>
                </div>
                <div className="max-h-80 overflow-y-auto divide-y divide-border">
                  {notifs.length === 0 ? <p className="p-4 text-sm text-muted-foreground">No notifications</p> :
                    notifs.map((n) => (
                      <div key={n.id} data-testid={`notif-${n.id}`} className={`p-3 text-sm ${n.read ? "opacity-60" : "bg-accent/40"}`}>
                        <p>{n.message}</p>
                      </div>
                    ))}
                </div>
              </PopoverContent>
            </Popover>
            <Button variant="ghost" size="icon" data-testid="logout-btn" onClick={() => { logout(); navigate("/admin"); }}><LogOut className="h-5 w-5" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Tabs defaultValue="overview">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="trips" data-testid="tab-trips">Trips</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="tab-feedback">Feedback</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            <div className="grid sm:grid-cols-3 gap-4">
              <StatCard icon={<Boxes className="h-5 w-5" />} label="Total trips" value={stats?.total_trips ?? "—"} />
              <StatCard icon={<Users className="h-5 w-5" />} label="Total bookings" value={stats?.total_bookings ?? "—"} />
              <StatCard icon={<Bell className="h-5 w-5" />} label="Unread alerts" value={stats?.unread_notifications ?? "—"} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {(stats?.trips || []).map((t) => (
                <Card key={t.id} className="p-5 space-y-3" data-testid={`overview-trip-${t.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-sm">{t.title}</p>
                    <Badge className={STATUS_STYLE[t.status] || "bg-muted"}>{statusLabel(TRIP_STATUS, t.status, lang)}</Badge>
                  </div>
                  <CapacityMeter capacity={t.capacity_kg} used={t.used_capacity_kg} remaining={t.remaining_capacity_kg} />
                </Card>
              ))}
            </div>
            <Card className="p-5 space-y-2">
              <p className="font-semibold">Setup note</p>
              <p className="text-sm text-muted-foreground">Go to the <b>Trips</b> tab and click <b>New Trip</b> to create a trip. Set the direction, receive deadline &amp; timezone, baggage capacity (kg), standard fee per kg (GBP), bulky pricing rule, and the £→IDR exchange rate. Edit any trip anytime to update addresses, fees, capacity, or status. Customer bookings appear under <b>Bookings</b> where you can enter reweighed weight, customs, delivery/AWB or pick-up time, add tracking updates, and mark completed. Use <b>Export</b> for the .xlsx report.</p>
              <p className="text-sm text-muted-foreground">Contacts: <b>{CONTACT.managers}</b> · WhatsApp {CONTACT.whatsapp} · UK {CONTACT.uk_phone}</p>
            </Card>
          </TabsContent>

          {/* Trips */}
          <TabsContent value="trips" className="mt-6 space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-display font-bold text-xl">Trips</h2>
              <Button data-testid="new-trip-btn" onClick={() => setTripDialog({ open: true, trip: null })}><Plus className="h-4 w-4 mr-1" /> New Trip</Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {trips.map((t) => (
                <Card key={t.id} className="p-5 space-y-3" data-testid={`admin-trip-${t.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{t.title}</p>
                      <p className="text-sm text-primary">{DIRECTION_LABEL[t.direction]}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={t.active === false ? "bg-muted text-muted-foreground" : "bg-emerald-500/15 text-emerald-600"} data-testid={`trip-active-badge-${t.id}`}>{t.active === false ? "Inactive" : "Active"}</Badge>
                      <Badge className={t.order_open === false ? "bg-amber-500/15 text-amber-600" : "bg-blue-500/15 text-blue-600"} data-testid={`trip-orders-badge-${t.id}`}>{t.order_open === false ? "Orders closed" : "Orders open"}</Badge>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-0.5">
                    <p>Deadline: {fmtDate(t.receive_deadline, lang)} ({t.deadline_timezone === "birmingham" ? "BHX" : "WIB"})</p>
                    <p>Fee: {fmtGBP(t.standard_fee_gbp_per_kg)}/kg · Bulky: {t.bulky_pricing_type} · £1={t.exchange_rate_gbp_to_idr} IDR</p>
                  </div>
                  <CapacityMeter capacity={t.capacity_kg} used={t.used_capacity_kg} remaining={t.remaining_capacity_kg} />
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" data-testid={`edit-trip-${t.id}`} onClick={() => setTripDialog({ open: true, trip: t })}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                    <Button variant="outline" size="sm" data-testid={`orders-trip-${t.id}`} onClick={() => setTripOrders(t, t.order_open === false)}>{t.order_open === false ? "Open orders" : "Close orders"}</Button>
                    <Button variant="outline" size="sm" data-testid={`active-trip-${t.id}`} onClick={() => setTripActive(t, t.active === false)}>{t.active === false ? "Activate" : "Set inactive"}</Button>
                    <Button variant="ghost" size="sm" data-testid={`delete-trip-${t.id}`} onClick={() => deleteTrip(t.id)}><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Bookings */}
          <TabsContent value="bookings" className="mt-6 space-y-4">
            <Card className="p-4 grid sm:grid-cols-4 gap-3">
              <div className="relative sm:col-span-1">
                <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                <Input data-testid="booking-search" className="pl-9" placeholder="Search name / ref / phone"
                  value={filters.search} onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))} />
              </div>
              <FilterSelect testid="filter-trip" value={filters.trip_id} onChange={(v) => setFilters((p) => ({ ...p, trip_id: v }))}
                placeholder="All trips" options={[{ v: "all", l: "All trips" }, ...trips.map((t) => ({ v: t.id, l: t.title }))]} />
              <FilterSelect testid="filter-direction" value={filters.direction} onChange={(v) => setFilters((p) => ({ ...p, direction: v }))}
                placeholder="All routes" options={[{ v: "all", l: "All routes" }, { v: "bhx_gresik", l: DIRECTION_LABEL.bhx_gresik }, { v: "gresik_bhx", l: DIRECTION_LABEL.gresik_bhx }]} />
              <FilterSelect testid="filter-status" value={filters.status} onChange={(v) => setFilters((p) => ({ ...p, status: v }))}
                placeholder="All statuses" options={[{ v: "all", l: "All statuses" }, ...Object.keys(TRACK_STATUS).map((s) => ({ v: s, l: statusLabel(TRACK_STATUS, s, lang) }))]} />
            </Card>

            <Card className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reference</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Trip</TableHead>
                    <TableHead>Weight</TableHead>
                    <TableHead>Fee</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Pay</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No bookings</TableCell></TableRow>
                  ) : bookings.map((b) => (
                    <TableRow key={b.id} data-testid={`booking-row-${b.reference}`} className="cursor-pointer hover:bg-accent/40" onClick={() => setBookingDialog({ open: true, booking: b })}>
                      <TableCell className="font-mono text-xs">{b.reference} {b.flagged && <Badge variant="destructive" className="ml-1">!</Badge>}</TableCell>
                      <TableCell>{b.customer_name}</TableCell>
                      <TableCell className="text-xs">{DIRECTION_LABEL[b.trip?.direction]}</TableCell>
                      <TableCell className="text-xs">{b.estimated_weight_kg}{b.adjusted_weight_kg != null ? ` → ${b.adjusted_weight_kg}` : ""} kg</TableCell>
                      <TableCell className="text-xs">{fmtGBP(b.final_fee_gbp ?? b.estimated_fee_gbp)}</TableCell>
                      <TableCell><Badge className={STATUS_STYLE[b.status] || "bg-muted"}>{statusLabel(TRACK_STATUS, b.status, lang)}</Badge></TableCell>
                      <TableCell><Badge variant={b.payment_status === "paid" ? "default" : "outline"}>{b.payment_status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Feedback */}
          <TabsContent value="feedback" className="mt-6 space-y-4">
            <h2 className="font-display font-bold text-xl">Customer Feedback</h2>
            {feedbackBookings.length === 0 ? (
              <Card className="p-8 text-center text-muted-foreground" data-testid="no-feedback">No feedback yet.</Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {feedbackBookings.map((b) => (
                  <Card key={b.id} className="p-5 space-y-2" data-testid={`feedback-${b.reference}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{b.customer_name}</span>
                      {b.feedback.satisfied
                        ? <Badge className="bg-emerald-500/15 text-emerald-600"><Smile className="h-3.5 w-3.5 mr-1" /> Satisfied</Badge>
                        : <Badge variant="destructive"><Frown className="h-3.5 w-3.5 mr-1" /> Not satisfied</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground font-mono">{b.reference}</p>
                    {b.feedback.comment && <p className="text-sm">"{b.feedback.comment}"</p>}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="settings">
            <SettingsTab />
          </TabsContent>
        </Tabs>
      </main>

      <TripDialog open={tripDialog.open} trip={tripDialog.trip}
        onOpenChange={(o) => setTripDialog((p) => ({ ...p, open: o }))} onSaved={reloadAll} />
      <BookingDialog open={bookingDialog.open} booking={bookingDialog.booking}
        onOpenChange={(o) => setBookingDialog((p) => ({ ...p, open: o }))} onSaved={reloadAll} />
    </div>
  );
}

const StatCard = ({ icon, label, value }) => (
  <Card className="p-5 flex items-center gap-4">
    <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary grid place-items-center">{icon}</div>
    <div>
      <p className="text-2xl font-bold font-display">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  </Card>
);

const FilterSelect = ({ testid, value, onChange, placeholder, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
    </SelectContent>
  </Select>
);
